import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, Loader2, Pause, Play, RotateCcw, SkipForward, Square, Volume2, VolumeX } from "lucide-react";
import { API, api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { LanguageSelect } from "@/components/LanguageSelect";

const POSTER = "/tour/news.jpg";

// MiLu's voices, described in the visitor's own language
const VOICES = [
  { id: "nova", name: "Nova", plain: "warm" },
  { id: "shimmer", name: "Shimmer", plain: "gentle" },
  { id: "alloy", name: "Alloy", plain: "clear" },
  { id: "echo", name: "Echo", plain: "steady" },
  { id: "fable", name: "Fable", plain: "storyteller" },
  { id: "onyx", name: "Onyx", plain: "deep" },
];

// A 60-second tour of ChatMiLu: real screens, MiLu talking over them, in any language.
// where this viewing happened, so the plans page can be told apart from everywhere else
const placeNow = () => {
  const path = window.location.pathname;
  if (path.startsWith("/pricing")) return "pricing";
  if (path.startsWith("/tour")) return "tour";
  return "other";
};

// a friend's code carried on a shared tour link, remembered until they sign up
const friendCode = () => {
  const asked = (new URLSearchParams(window.location.search).get("ref") || "").toUpperCase();
  if (asked) localStorage.setItem("cml_tour_ref", asked);
  return asked || localStorage.getItem("cml_tour_ref") || "";
};

export const TourPlayer = ({ compact = false, onScene = null, page = "" }) => {
  const { lang, setLang, languages } = useI18n();
  const { user } = useAuth();
  const where = page || placeNow();
  const [scenes, setScenes] = useState([]);
  const [say, setSay] = useState({});   // the player's own labels, in this language
  const [total, setTotal] = useState(40);
  const [at, setAt] = useState(-1);          // -1 is the poster, before anything plays
  const [held, setHeld] = useState(false);
  const [quiet, setQuiet] = useState(false);
  const [busy, setBusy] = useState(false);
  const [through, setThrough] = useState(0); // 0 to 1 through the scene on screen
  const [voice, setVoice] = useState(() => localStorage.getItem("cml_tour_voice") || "nova");
  const [tasting, setTasting] = useState(false);
  const player = useRef(null);
  const timer = useRef(null);
  const watch = useRef("");   // one id per viewing, so we can see where people stop
  const ahead = useRef(null);  // the next scene's voice, fetched early
  const taste = useRef(null);  // the sample line, played on its own
  const opened = useRef(false);  // the first scene of a viewing is the play, wherever it starts

  useEffect(() => {
    api.get("/tour", { params: { lang } }).then(({ data }) => {
      setScenes(data.scenes);
      setSay(data.ui || {});
      setTotal(Math.round(data.seconds / 5) * 5);
    }).catch(() => {});
    return () => {
      clearTimeout(timer.current);
      player.current?.pause();
    };
  }, [lang]);

  // a shared link is remembered the moment it is opened, whether or not anyone presses play
  useEffect(() => { friendCode(); }, []);

  // once someone is signed in, the viewings they had beforehand become theirs
  useEffect(() => {
    if (!user) return;
    const sessions = JSON.parse(localStorage.getItem("cml_tour_watch") || "[]");
    const ref = localStorage.getItem("cml_tour_ref") || "";
    if (!sessions.length && !ref) return;
    api.post("/tour/claim", { sessions, ref })
      .then(() => {
        localStorage.removeItem("cml_tour_watch");
        localStorage.removeItem("cml_tour_ref");
      })
      .catch(() => {});
  }, [user]);

  const scene = at >= 0 ? scenes[at] : null;

  // a link can point at one chapter: /tour?scene=news
  const asked = new URLSearchParams(window.location.search).get("scene") || "";
  const wanted = useMemo(() => {
    if (!asked || scenes.length === 0) return 0;
    const byKey = scenes.findIndex((s) => s.key === asked);
    if (byKey > 0) return byKey;
    const n = Number(asked);
    return Number.isInteger(n) && n > 0 && n < scenes.length ? n : 0;
  }, [asked, scenes]);

  // a quiet note of how far this viewing got — never in the way of playing
  const note = useCallback((event, index) => {
    if (!watch.current) return;
    api.post("/tour/watched", { session: watch.current, event, scene: index, lang,
                                page: where, ref: friendCode() })
      .catch(() => {});
  }, [lang, where]);

  // one scene: MiLu's line if she can be heard, a plain timer if not
  const run = useCallback((index, muted) => {
    clearTimeout(timer.current);
    player.current?.pause();
    if (index >= scenes.length) {
      note("finish", scenes.length - 1);
      onScene?.(null);
      setAt(-1);
      setThrough(0);
      return;
    }
    note(opened.current ? "scene" : "play", index);
    opened.current = true;
    onScene?.(scenes[index]);
    setAt(index);
    setThrough(0);
    setBusy(!muted);
    const seconds = scenes[index].seconds;
    const onward = () => run(index + 1, muted);

    if (muted) {
      const started = Date.now();
      const tick = () => {
        const done = (Date.now() - started) / (seconds * 1000);
        setThrough(Math.min(1, done));
        timer.current = done >= 1 ? setTimeout(onward, 0) : setTimeout(tick, 100);
      };
      tick();
      return;
    }

    // the next line is fetched while this one plays, so scenes run on without a gap
    const clip = (i) => `${API}/tour/say/${i}?lang=${lang}&voice=${voice}`;
    const audio = ahead.current?.src === clip(index)
      ? ahead.current : new Audio(clip(index));
    if (index + 1 < scenes.length) {
      ahead.current = new Audio(clip(index + 1));
      ahead.current.preload = "auto";
      ahead.current.load();
    }
    player.current = audio;
    audio.onplaying = () => setBusy(false);
    audio.ontimeupdate = () => {
      if (audio.duration) setThrough(Math.min(1, audio.currentTime / audio.duration));
    };
    audio.onended = () => {
      // hold the picture for the rest of the scene, so the tour runs to the second
      const left = (seconds - (audio.duration || seconds)) * 1000;
      timer.current = left > 150 ? setTimeout(onward, left) : setTimeout(onward, 0);
    };
    audio.onerror = () => {  // no voice available — keep the tour moving silently
      setBusy(false);
      timer.current = setTimeout(onward, seconds * 1000);
    };
    audio.play().catch(() => {
      setBusy(false);
      timer.current = setTimeout(onward, seconds * 1000);
    });
  }, [scenes, lang, voice, note, onScene]);

  // from the top, or straight to the chapter a visitor came for
  const start = (index = 0) => {
    if (!scenes.length) return;  // nothing to show yet, so nothing to report either
    if (at < 0 || !watch.current) {
      watch.current = `w_${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
      opened.current = false;
      // kept so a viewing watched before signing up can still be tied to the new account
      const seen = JSON.parse(localStorage.getItem("cml_tour_watch") || "[]");
      localStorage.setItem("cml_tour_watch",
        JSON.stringify([watch.current, ...seen.filter((s) => s !== watch.current)].slice(0, 5)));
    }
    setHeld(false);
    run(Math.max(0, Math.min(index, scenes.length - 1)), quiet);
  };

  const hold = () => {
    if (held) {
      setHeld(false);
      player.current?.play().catch(() => {});
      return;
    }
    setHeld(true);
    clearTimeout(timer.current);
    player.current?.pause();
  };

  // one line in the chosen voice, before committing to the whole tour
  const sample = async () => {
    if (taste.current) {
      taste.current.pause();
      taste.current = null;
      setTasting(false);
      return;
    }
    clearTimeout(timer.current);   // quiet whatever is playing, without touching the mute switch
    player.current?.pause();
    setTasting(true);
    const clip = new Audio(`${API}/tour/say/0?lang=${lang}&voice=${voice}`);
    taste.current = clip;
    clip.onended = () => { taste.current = null; setTasting(false); };
    clip.onerror = () => { taste.current = null; setTasting(false); };
    try {
      await clip.play();
    } catch {
      taste.current = null;
      setTasting(false);
    }
  };

  const pickVoice = (next) => {
    if (taste.current) {
      taste.current.pause();
      taste.current = null;
      setTasting(false);
    }
    setVoice(next);
    localStorage.setItem("cml_tour_voice", next);
    ahead.current = null;
    if (at >= 0) run(at, quiet);
  };

  const hush = () => {
    const next = !quiet;
    setQuiet(next);
    if (at >= 0) run(at, next);
  };

  return (
    <div
      data-testid="tour-player"
      className={`group relative overflow-hidden rounded-3xl border border-white/10 bg-[#07080B] ${
        compact ? "" : "shadow-[0_30px_80px_-40px_rgba(26,125,255,0.45)]"}`}
    >
      <div className="relative aspect-[16/9] w-full">
        {scenes.map((s, i) => (
          s.shot ? (
            <img
              key={s.key}
              src={s.shot}
              alt=""
              aria-hidden={at !== i}
              className={`absolute inset-0 h-full w-full object-cover object-top transition-opacity duration-700 ${
                at === i ? "opacity-100 tour-drift" : "opacity-0"}`}
            />
          ) : null
        ))}
        {(at < 0 || !scene?.shot) && (
          <img src={POSTER} alt="" className="absolute inset-0 h-full w-full object-cover object-top opacity-25" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-[#07080B] via-[#07080B]/35 to-transparent" />

        {at < 0 ? (
          <div className="absolute inset-0 flex flex-col items-start justify-end gap-3 p-6 sm:p-8">
            <p className="text-[11px] font-semibold uppercase tracking-widest text-brand">
              {say.tour || `The ${total}-second tour`}
            </p>
            <h3 className="max-w-lg font-display text-2xl font-bold leading-tight sm:text-3xl">
              {say.everything || `Everything ChatMiLu does, in ${total} seconds`}
            </h3>
            <button
              data-testid="tour-play"
              onClick={() => start(wanted)}
              disabled={!scenes.length}
              className="mt-1 inline-flex min-h-[46px] items-center gap-2.5 rounded-full bg-brand px-6 text-sm font-semibold text-white transition-transform hover:bg-brand-hover active:scale-95"
            >
              <Play className="h-4 w-4" />
              {wanted > 0 && scenes[wanted]
                ? `${say.watch_from || "Watch from"} “${scenes[wanted].title}”`
                : (say.watch || "Watch the tour")}
            </button>
            <div className="flex flex-wrap items-center gap-2">
              <span className="relative inline-flex items-center">
                <select
                  data-testid="tour-voice"
                  value={voice}
                  onChange={(e) => pickVoice(e.target.value)}
                  aria-label="Narrator's voice"
                  className="min-h-[34px] appearance-none rounded-full border border-white/15 bg-black/50 ps-3 pe-8 text-xs text-ink outline-none transition-colors hover:border-brand/50"
                >
                  {VOICES.map((v) => (
                    <option key={v.id} value={v.id} className="bg-[#0A0A0B]">
                      {v.name} · {say[`v_${v.id}`] || v.plain}
                    </option>
                  ))}
                </select>
                <ChevronDown className="pointer-events-none absolute end-3 h-3.5 w-3.5 text-ink-soft" />
              </span>
              <button
                data-testid="tour-voice-sample"
                onClick={sample}
                title="Hear one line in this voice"
                className="inline-flex min-h-[34px] items-center gap-1.5 rounded-full border border-white/15 bg-black/40 px-3 text-[11px] text-ink-soft transition-colors hover:border-brand/50 hover:text-brand"
              >
                {tasting ? <Square className="h-3 w-3" /> : <Volume2 className="h-3 w-3" />}
                {tasting ? (say.stop || "Stop") : (say.hear || "Hear it")}
              </button>
              <span className="text-[11px] text-ink-soft">{say.narrates || "MiLu narrates in"}</span>
              <LanguageSelect
                languages={languages}
                value={lang}
                onChange={setLang}
                compact
                testId="tour-language"
              />
            </div>
          </div>
        ) : (
          <div className="absolute inset-0 flex flex-col justify-end p-5 sm:p-7">
            <p className="text-[11px] font-semibold uppercase tracking-widest text-brand"
               data-testid="tour-scene-title">
              {scene?.title}
            </p>
            <p className="mt-1.5 max-w-2xl font-display text-lg font-bold leading-snug sm:text-2xl"
               data-testid="tour-caption">
              {scene?.caption}
            </p>
            <div className="mt-4 flex items-center gap-2">
              <button
                data-testid="tour-hold"
                onClick={hold}
                aria-label={held ? "Play on" : "Hold it there"}
                className="rounded-full border border-white/15 bg-black/40 p-2 text-ink transition-colors hover:border-brand/60 hover:text-brand"
              >
                {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  : held ? <Play className="h-3.5 w-3.5" /> : <Pause className="h-3.5 w-3.5" />}
              </button>
              <button
                data-testid="tour-skip"
                onClick={() => run(at + 1, quiet)}
                aria-label="Next scene"
                className="rounded-full border border-white/15 bg-black/40 p-2 text-ink transition-colors hover:border-brand/60 hover:text-brand"
              >
                <SkipForward className="h-3.5 w-3.5" />
              </button>
              <button
                data-testid="tour-mute"
                onClick={hush}
                aria-label={quiet ? "Let MiLu talk" : "Silence MiLu"}
                className="rounded-full border border-white/15 bg-black/40 p-2 text-ink transition-colors hover:border-brand/60 hover:text-brand"
              >
                {quiet ? <VolumeX className="h-3.5 w-3.5" /> : <Volume2 className="h-3.5 w-3.5" />}
              </button>
              <button
                data-testid="tour-restart"
                onClick={start}
                aria-label="From the top"
                className="rounded-full border border-white/15 bg-black/40 p-2 text-ink transition-colors hover:border-brand/60 hover:text-brand"
              >
                <RotateCcw className="h-3.5 w-3.5" />
              </button>
              <span className="ms-auto text-[11px] tabular-nums text-ink-soft">
                {at + 1} / {scenes.length}
              </span>
            </div>
          </div>
        )}
      </div>

      <div className="flex flex-wrap gap-1.5 bg-[#07080B] px-3 pb-3 pt-3" data-testid="tour-chapters">
        <span className="me-1 self-center text-[10px] font-semibold uppercase tracking-widest text-ink-soft">
          {say.jump || "Jump to"}
        </span>
        {scenes.slice(1, scenes.length - 1).map((s, i) => (
          <button
            key={s.key}
            data-testid={`tour-chapter-${s.key}`}
            onClick={() => start(i + 1)}
            title={s.caption}
            className={`min-h-[26px] max-w-[190px] truncate rounded-full border px-2.5 text-[11px] transition-colors ${
              at === i + 1
                ? "border-brand bg-brand/15 text-brand"
                : "border-white/12 text-ink-soft hover:border-brand/50 hover:text-brand"}`}
          >
            {s.title}
          </button>
        ))}
      </div>

      <div className="flex gap-1 bg-[#07080B] px-2 pb-2" data-testid="tour-progress">
        {scenes.map((s, i) => (
          <span key={s.key} className="h-[3px] flex-1 overflow-hidden rounded-full bg-white/10">
            <span
              className="block h-full rounded-full bg-brand transition-[width] duration-200"
              style={{ width: at > i ? "100%" : at === i ? `${through * 100}%` : "0%" }}
            />
          </span>
        ))}
      </div>
    </div>
  );
};
